import { useEffect, useRef, useState } from 'react';
import { Icon } from '@luacentrix/icons';
import { IconButton } from '@luacentrix/ui';
import { useApp } from '../app-state';
import { THEME_ORDER, type ThemeName } from '../data/theme';
import { UI_STYLE_ORDER, type GlassCapability, type UiStyleName } from '../data/ui-style';
import { GlassSurface } from './GlassSurface';

const THEME_LABELS: Record<ThemeName, string> = { dark: 'Dark', light: 'Light' };

const STYLE_LABELS: Record<UiStyleName, string> = { legacy: 'Legacy UI', glass: 'Liquid Glass' };

function themeIcon(name: ThemeName): 'moon' | 'sun' {
  return name === 'dark' ? 'moon' : 'sun';
}

function styleIcon(style: UiStyleName): 'stack' | 'droplet' {
  return style === 'legacy' ? 'stack' : 'droplet';
}

function styleTitle(style: UiStyleName, glass: GlassCapability): string {
  if (style === 'legacy') return 'Legacy UI — solid surfaces';
  return glass.refraction
    ? 'Liquid Glass — refractive surfaces'
    : 'Liquid Glass — frosted surfaces, no refraction in this browser';
}

export function ThemePicker() {
  const { theme, setTheme, uiStyle, setUiStyle, glass } = useApp();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (open === false) return;
    const onPointerDown = (event: PointerEvent) => {
      const node = wrapRef.current;
      if (node !== null && !node.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false);
        triggerRef.current?.focus();
        return;
      }
      const forward = event.key === 'ArrowDown' || event.key === 'ArrowRight';
      const backward = event.key === 'ArrowUp' || event.key === 'ArrowLeft';
      if (!forward && !backward) return;
      const items = panelRef.current?.querySelectorAll<HTMLButtonElement>('[role="menuitemradio"]');
      if (items === undefined || items.length === 0) return;
      event.preventDefault();
      const index = Array.prototype.indexOf.call(items, document.activeElement);
      const step = forward ? 1 : -1;
      const next = (index + step + items.length) % items.length;
      items[next]?.focus();
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  useEffect(() => {
    if (open === false) return;
    panelRef.current?.querySelector<HTMLButtonElement>('[role="menuitemradio"]')?.focus();
  }, [open]);

  const closeAndRefocus = (): void => {
    setOpen(false);
    triggerRef.current?.focus();
  };

  return (
    <div className="theme-picker" ref={wrapRef}>
      <IconButton
        ref={triggerRef}
        name={themeIcon(theme)}
        label="Change theme"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen(current => !current)}
      />
      {open ? (
        <div className="theme-picker__panel">
          <GlassSurface className="theme-picker__glass" padding="0px" radius={16} frost={22} bend={24}>
            <div className="theme-picker__menu" role="menu" aria-label="Theme" ref={panelRef}>
              {THEME_ORDER.map(name => (
                <button
                  aria-checked={theme === name}
                  aria-label={THEME_LABELS[name]}
                  className="theme-picker__option"
                  key={name}
                  onClick={() => {
                    setTheme(name);
                    closeAndRefocus();
                  }}
                  role="menuitemradio"
                  title={THEME_LABELS[name]}
                  type="button"
                >
                  <Icon name={themeIcon(name)} size="sm" />
                </button>
              ))}
              <span aria-hidden="true" className="theme-picker__divider" />
              {UI_STYLE_ORDER.map(name => (
                <button
                  aria-checked={uiStyle === name}
                  aria-label={STYLE_LABELS[name]}
                  className="theme-picker__option"
                  key={name}
                  onClick={() => {
                    setUiStyle(name);
                    closeAndRefocus();
                  }}
                  role="menuitemradio"
                  title={styleTitle(name, glass)}
                  type="button"
                >
                  <Icon name={styleIcon(name)} size="sm" />
                </button>
              ))}
            </div>
          </GlassSurface>
        </div>
      ) : null}
    </div>
  );
}
